/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import useAsyncRequest from "hooks/useAsyncRequest";
import { getErrorMessage } from "utils/errorHelpers";
import type { BillingAccountDto, BillingAccountSubscriptionDto, SubscriptionTransferPreviewDto } from "client/api-v2";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { SubscriptionSelector } from "areas/subscriptions/components/shared/SubscriptionSelector";
import type { SubscriptionOption } from "areas/subscriptions/components/shared/SubscriptionSelector";
import { assignSubscriptionToBillingAccount, getSubscriptionTransferPreview } from "../services/billingAccountService";
import { BillingAccountSelector } from "./BillingAccountSelector";
import type { BillingAccountOption } from "./BillingAccountSelector";

interface LinkOrTransferSubscriptionDialogProps {
  open: boolean;
  billingAccount?: BillingAccountDto;
  currentAccountSubscriptionIds?: ReadonlySet<string>;
  subscription?: BillingAccountSubscriptionDto;
  onCancel: () => void;
  onSaved: () => void;
}

interface FormValues {
  billingAccount: BillingAccountOption | null;
  subscription: SubscriptionOption | null;
}

const formId = "link-or-transfer-subscription-form";
const emptyValues: FormValues = { billingAccount: null, subscription: null };

/**
 * What else the write moves. The draft quotes for a subscription bill to whichever account it is
 * on, so they follow it; a published quote keeps its own, because the customer has read it there.
 * @param count
 */
function draftQuotesMoving(count: number): string {
  const quotes = count === 1 ? "1 draft quote" : `${count} draft quotes`;
  const verb = count === 1 ? "moves" : "move";
  return `${quotes} for this subscription ${verb} to the new billing account.`;
}

function defaultValuesFor(
  billingAccount?: BillingAccountDto,
  subscription?: BillingAccountSubscriptionDto
): FormValues {
  return {
    billingAccount: billingAccount
      ? {
          id: billingAccount.code,
          code: billingAccount.code,
          name: billingAccount.commonName ?? billingAccount.legalName,
          isCurrentAccount: true,
        }
      : null,
    subscription: subscription
      ? {
          id: subscription.id,
          serial: subscription.serial,
          friendlyName: subscription.friendlyName,
          linkStatus: "linkedToThisAccount",
        }
      : null,
  };
}

export function LinkOrTransferSubscriptionDialog({
  open,
  billingAccount,
  currentAccountSubscriptionIds,
  subscription,
  onCancel,
  onSaved,
}: LinkOrTransferSubscriptionDialogProps) {
  const [serverError, setServerError] = useState<string | undefined>();
  const { isAuthorized } = useRbacContext();
  const canSave = isAuthorized(RbacPolicies.Admin.ReadWrite);
  const mode: "attach" | "transfer" = subscription && billingAccount ? "transfer" : "attach";

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({ defaultValues: emptyValues });

  useEffect(() => {
    if (open) {
      reset(defaultValuesFor(billingAccount, subscription));
      setServerError(undefined);
    }
  }, [open, billingAccount, subscription, reset]);

  // The subscription that is moving: fixed by the caller on a transfer, and chosen in the form on
  // an assign. The prop is read first, because the form is reset from it before `useWatch` has
  // subscribed and the reset therefore reaches no watcher.
  const chosenSubscription = useWatch({ control, name: "subscription" });
  const selectedSubscriptionId = subscription?.id ?? chosenSubscription?.id ?? null;
  const loadTransferPreview = useCallback(
    (): Promise<SubscriptionTransferPreviewDto | null> =>
      open && selectedSubscriptionId !== null
        ? getSubscriptionTransferPreview(selectedSubscriptionId)
        : Promise.resolve(null),
    [open, selectedSubscriptionId]
  );
  // A failed read leaves the count unsaid rather than blocking the transfer. The write moves the
  // drafts either way, and the warning below still stands.
  const { data: transferPreview } = useAsyncRequest(loadTransferPreview);
  const draftQuoteCount = transferPreview?.draftQuoteCount ?? 0;
  const chosenBillingAccount = useWatch({ control, name: "billingAccount" });
  const destinationAccountCode = chosenBillingAccount?.code ?? billingAccount?.code;
  const liveQuoteToCancel =
    transferPreview?.liveQuoteOdNumber && transferPreview.liveQuoteBillingAccountCode !== destinationAccountCode
      ? transferPreview.liveQuoteOdNumber
      : undefined;

  const onSubmit = async (values: FormValues) => {
    setServerError(undefined);
    const targetSubscriptionId = values.subscription!.id;
    const targetBillingAccountCode = values.billingAccount!.code;

    try {
      await assignSubscriptionToBillingAccount(targetSubscriptionId, targetBillingAccountCode);
      onSaved();
    } catch (e) {
      setServerError(getErrorMessage(e));
    }
  };

  const cancel = () => !isSubmitting && onCancel();

  const title = mode === "transfer" ? "Transfer subscription" : "Assign subscription to billing account";

  return (
    <Dialog open={open} onClose={cancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            {serverError && (
              <Grid size={12}>
                <Alert severity="error">{serverError}</Alert>
              </Grid>
            )}

            <Grid size={12}>
              <BillingAccountSelector<FormValues>
                name="billingAccount"
                control={control}
                rules={{ validate: (value) => !!value || "Billing account is required" }}
                required
                disabled={!!billingAccount && !subscription}
                currentAccountCode={billingAccount?.code}
                hideResellers={true}
              />
            </Grid>

            <Grid size={12}>
              <SubscriptionSelector<FormValues>
                name="subscription"
                control={control}
                rules={{ validate: (value) => !!value || "Subscription is required" }}
                required
                disabled={!!subscription}
                currentAccountSubscriptionIds={currentAccountSubscriptionIds}
              />
            </Grid>

            {draftQuoteCount > 0 && (
              <Grid size={12}>
                <Alert severity="info">{draftQuotesMoving(draftQuoteCount)}</Alert>
              </Grid>
            )}

            {liveQuoteToCancel && (
              <Grid size={12}>
                <Alert severity="warning">
                  Quote {liveQuoteToCancel} is live with the customer and will be cancelled. Create a new quote on the
                  new billing account.
                </Alert>
              </Grid>
            )}

            <Grid size={12}>
              <Alert severity="warning">
                Check the link carefully. An incorrectly linked subscription exposes sensitive billing account data of
                one customer to a different, wrong customer.
              </Alert>
            </Grid>
          </Grid>
        </form>

        <Grid size={12} sx={{ pt: 3 }}>
          <Divider />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" form={formId} variant="contained" color="primary" disabled={isSubmitting || !canSave}>
          {mode === "transfer"
            ? isSubmitting
              ? "Transferring..."
              : "Transfer Subscription"
            : isSubmitting
            ? "Assigning..."
            : "Assign Subscription"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
